import axios from './axiosInstance';
import { causes, machines, templates } from './mockData';
import {
  Cause,
  Issue,
  Machine,
  Routine,
  SolutionGridItem,
  SolutionGridRequestDto,
  SolutionGridVersion,
  Template,
  QueryParams,
  User,
  Company,
  CreateLicensePayload,
  License,
  TemplateResourceImages,
} from '../types';
import { Event } from '../types/events';

export const getMachine = async (params?: QueryParams) => {
  if (process.env.REACT_APP_ENABLE_MOCK === 'true') {
    return await Promise.resolve({ data: machines });
  }

  const res = await axios.get(
    `/machine?search=${params?.search || ''}&pageSize=${params?.pageSize || 1000}&pageNumber=${params?.pageNumber || 0}`
  );

  return res.data;
};

export const getMachineById = async (id: string) => {
  const res = await axios.get(`/machine/${id}`);

  return res.data;
};

export const createMachine = async (payload: Machine) => {
  if (process.env.REACT_APP_ENABLE_MOCK === 'true') {
    return await Promise.resolve({ data: payload });
  }

  const res = await axios.post('/machine', payload);

  return res.data;
};

export const updateMachine = async (payload: Machine) => {
  const res = await axios.put('/machine', payload);

  return res.data;
};

export const deleteMachine = async (id: string) => {
  const res = await axios.delete(`/machine/${id}`);

  return res.data;
};

export const getTemplates = async (params?: QueryParams) => {
  if (process.env.REACT_APP_ENABLE_MOCK === 'true') {
    return await Promise.resolve({ data: templates });
  }

  const res = await axios.get(
    `${process.env.REACT_APP_SERVICE_URL}/template?search=${params?.search || ''}&pageSize=${params?.pageSize || 1000}&pageNumber=${params?.pageNumber || 0}`
  );

  return res.data.data.map((t: Template) => ({
    ...t,
    issues: t.issues || [],
    causes: t.causes || [],
    machines: t.machines || [],
  }));
};

export const getTemplateById = async (id: string) => {
  if (process.env.REACT_APP_ENABLE_MOCK === 'true') {
    return await Promise.resolve({ data: templates });
  }

  const res = await axios.get(`/template/${id}`);

  return res.data;
};

export const getTemplateImages = async (
  templateID: string,
  params?: QueryParams
): Promise<{ data: TemplateResourceImages }> => {
  const res = await axios.get(
    `/template/${templateID}/images?pageSize=${params?.pageSize || 1000}&pageNumber=${params?.pageNumber || 0}`
  );

  return res.data;
};

export const createTemplate = async (payload: Template) => {
  const res = await axios.post('/template', { ...payload, templateID: null });

  return res.data;
};

export const updateTemplate = async (payload: Template) => {
  const res = await axios.put('/template', payload);

  return res.data;
};

export const deleteTemplates = async (id: string) => {
  const res = await axios.delete(`/template/${id}`);

  return res.data;
};

export const copyTemplate = async (id: string) => {
  const res = await axios.post(`/template/copy/${id}`);

  return res.data?.data;
};

export const linkMachines = async (payload: {
  machines: Machine[];
  templateID: string;
}) => {
  const res = await axios.post(
    `/template/linkmachines/${payload.templateID}`,
    payload.machines
  );

  return res.data;
};

export const unlinkMachine = async (machineID: string) => {
  const res = await axios.delete(`/machine/unlink/${machineID}`);

  return res.data;
};

export const getMachinesByTemplateId = async (templateID: string) => {
  const res = await axios.get(`/machine/template/${templateID}`);

  return res.data;
};

export const getIssues = async (params?: QueryParams) => {
  const res = await axios.get(
    `/issue?search=${params?.search || ''}&pageSize=${params?.pageSize || 1000}&pageNumber=${params?.pageNumber || 0}`
  );

  return res.data;
};

export const getIssuesByImage = async (payload: {
  templateId: string;
  fileId: string;
}) => {
  const res = await axios.post('/issue/searchByImage', payload);

  return res.data;
};

export const getIssuesByTemplateId = async (
  templateID: string,
  params?: QueryParams
) => {
  const res = await axios.get(
    `/issue/template/${templateID}?activeStatus=ACTIVE&isSuggestion=&suggestionStatus=&search=${params?.search || ''}&pageSize=${params?.pageSize || 1000}&pageNumber=${params?.pageNumber || 0}`
  );

  return res.data;
};

export const createIssue = async (payload: Issue) => {
  if (process.env.REACT_APP_ENABLE_MOCK === 'true') {
    return await Promise.resolve({ data: payload });
  }
  const res = await axios.post('/issue', payload);

  return res.data;
};

export const updateIssue = async (payload: Issue) => {
  if (process.env.REACT_APP_ENABLE_MOCK === 'true') {
    return await Promise.resolve({ data: payload });
  }
  const res = await axios.put('/issue', payload);

  return res.data;
};

export const deleteIssue = async (issueId: string) => {
  const res = await axios.delete(`/issue/${issueId}`);

  return res.data;
};

export const getCauses = async (params?: QueryParams) => {
  if (process.env.REACT_APP_ENABLE_MOCK === 'true') {
    return await Promise.resolve({ data: causes });
  }

  const res = await axios.get(
    `/cause?search=${params?.search || ''}&pageSize=${params?.pageSize || 1000}&pageNumber=${params?.pageNumber || 0}`
  );

  return res.data;
};

export const getCausesByTemplateId = async (
  templateID: string,
  params?: QueryParams
) => {
  const res = await axios.get(
    `/cause/template/${templateID}?activeStatus=ACTIVE&isSuggestion=&suggestionStatus=&search=${params?.search || ''}&pageSize=${params?.pageSize || 1000}&pageNumber=${params?.pageNumber || 0}`
  );

  return res.data;
};

export const createCause = async (payload: Cause) => {
  if (process.env.REACT_APP_ENABLE_MOCK === 'true') {
    return await Promise.resolve({ data: payload });
  }

  const res = await axios.post('/cause', payload);

  return res.data;
};

export const updateCause = async (payload: Cause) => {
  const res = await axios.put('/cause', payload);

  return res.data;
};

export const deleteCause = async (causeId: string) => {
  const res = await axios.delete(`/cause/${causeId}`);

  return res.data;
};

export const createSolutionGrid = async (
  payload: SolutionGridRequestDto,
  templateID: string
) => {
  const res = await axios.post(`/solutiongrid/${templateID}`, payload);

  return res.data;
};

export const updateSolutionGrid = async (
  payload: SolutionGridItem[],
  templateID: string
) => {
  const res = await axios.put(`/solutiongrid/${templateID}`, payload);

  return res.data;
};

export const getSolutionGridByTemplateId = async (
  templateID: string,
  versionID?: string
) => {
  const res = await axios.get(
    `/solutiongrid/template/${templateID}?versionID=${versionID || ''}`
  );

  return res.data;
};

export const updateSolutionGridVersion = async (
  templateID: string,
  versions?: SolutionGridVersion[]
) => {
  const res = await axios.put(`/solutiongrid/version/${templateID}`, versions);

  return res.data;
};

export const getRoutineByTemplateId = async (
  templateID: string,
  params?: QueryParams
) => {
  const res = await axios.get(
    `/routine/template/${templateID}?activeStatus=ACTIVE&isSuggestion=&suggestionStatus=&search=${params?.search || ''}&pageSize=${params?.pageSize || 1000}&pageNumber=${params?.pageNumber || 0}`
  );

  return res.data;
};

export const createRoutine = async (payload: Routine) => {
  const res = await axios.post('/routine', payload);

  return res.data;
};

export const updateRoutine = async (payload: Routine) => {
  const res = await axios.put('/routine', payload);

  return res.data;
};

export const deleteRoutine = async (id: string) => {
  const res = await axios.delete(`/routine/${id}`);

  return res.data;
};

export const login = async (payload: {
  username: string;
  password: string;
}) => {
  try {
    localStorage.removeItem('token');
    const res = await axios.post('/auth/login', payload);

    const data = res.data?.data || {};
    const { token } = data;
    const userId = data.userId || data.userID;
    const username = data.username || payload.username;
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('userId', String(userId));
      localStorage.setItem('username', username);
    }

    return data;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const resetPassword = async (payload: {
  userID: string | number;
  username: string;
  oldPassword?: string;
  password: string;
}) => {
  try {
    const res = await axios.post('/user/resetpassword', payload);
    return res.data?.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getUserInfo = async () => {
  const userId = localStorage.getItem('userId');
  const res = await axios.get(`/user/${userId}`);

  return res.data?.data;
};

export const getUsers = async () => {
  const res = await axios.get('/user?pageSize=1000');

  return res.data?.data;
};

export const createUser = async (payload: User) => {
  const res = await axios.post('/user', { ...payload, userId: 0 });

  return res.data?.data;
};

export const updateUser = async (payload: User) => {
  const res = await axios.put('/user', payload);

  return res.data?.data;
};

export const deleteUser = async (id: string) => {
  const res = await axios.delete(`/user/${id}`);

  return res.data?.data;
};
export const getCompanies = async () => {
  const res = await axios.get('/company?pageSize=1000');

  return res.data?.data;
};

export const createCompany = async (payload: Company) => {
  const res = await axios.post('/company', { ...payload, companyID: 0 });

  return res.data?.data;
};

export const updateCompany = async (payload: Company) => {
  const res = await axios.put(`/company/${payload.companyID}`, payload);

  return res.data?.data;
};

export const deleteCompany = async (id: string) => {
  const res = await axios.delete(`/company/${id}`);

  return res.data?.data;
};

export const createTrialLicense = async (payload: CreateLicensePayload) => {
  const res = await axios.post('/license/trial', payload);

  return res.data?.data;
};

export const convertLicense = async (
  licenseKey: string,
  payload: CreateLicensePayload
) => {
  const res = await axios.post(`/license/convert/${licenseKey}`, payload);

  return res.data?.data;
};

export const extendLicense = async (
  licenseKey: string,
  payload: CreateLicensePayload
) => {
  const res = await axios.post(`/license/extend/${licenseKey}`, payload);

  return res.data?.data;
};

export const terminateLicense = async (licenseKey: string) => {
  const res = await axios.post(`/license/terminate/${licenseKey}`, {});

  return res.data?.data;
};

export const updateExtraForLicense = async (
  licenseKey: string,
  license: License
) => {
  const res = await axios.post(`/license/updateExtra/${licenseKey}`, license);

  return res.data?.data;
};

export const getLicenseForCompany = async (companyID: string) => {
  const res = await axios.get(`/license/${companyID}`);

  return res.data?.data;
};

export const diagnoseByIssue = async (payload: any) => {
  const res = await axios.post('/solutiongrid/diagnose', payload);

  return res.data?.data;
};

export const getNotifications = async () => {
  const res = await axios.get('/notification');

  return res.data?.data;
};
export const reviewSuggestionIssue = async (payload: Issue) => {
  const res = await axios.post(
    `/issue/approvereject/${payload.issueID}`,
    payload
  );

  return res.data;
};

export const reviewSuggestionCause = async (payload: Cause) => {
  const res = await axios.post(
    `/cause/approvereject/${payload.causeID}`,
    payload
  );

  return res.data;
};

export const reviewSuggestionRoutine = async (payload: Routine) => {
  const res = await axios.post(
    `/routine/approvereject/${payload.routineID}`,
    payload
  );

  return res.data;
};

export const submitEvent = async (payload: Event) => {
  const res = await axios.post('/event', payload);

  return res.data;
};

export const getConversationLogs = async (
  type: 'diagnosis' | 'routine' | 'step' | '*'
) => {
  const res = await axios.get(`/event/conversations/${type}`);

  return res.data;
};

export const downloadCompanyLogs = async (companyName: string) => {
  const res = await axios.get(
    `/file/log/company/${encodeURIComponent(companyName)}`,
    { responseType: 'blob' }
  );

  const contentDisposition = res.headers['content-disposition'];
  let fileName = `knowledgehub-company-${companyName}.log`;
  if (contentDisposition) {
    const match = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );
    if (match && match[1]) {
      fileName = match[1].replace(/['"]/g, '');
    }
  }

  return { blob: res.data, fileName };
};

export const downloadAllLogs = async () => {
  const res = await axios.get('/file/log', { responseType: 'blob' });

  const contentDisposition = res.headers['content-disposition'];
  let fileName = 'knowledgehub-all.log';
  if (contentDisposition) {
    const match = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    );
    if (match && match[1]) {
      fileName = match[1].replace(/['"]/g, '');
    }
  }

  return { blob: res.data, fileName };
};
